import React, { useState, useEffect } from 'react';
import { Row, Col, Card, Button, Form, Badge, Alert, InputGroup, ListGroup, Nav, Tab } from 'react-bootstrap';
import { 
  FaSearch, FaFilePdf, FaFileExcel, FaIdCard, 
  FaCheckCircle, FaTimesCircle, FaQrcode, FaUserCheck, FaHistory,
  FaExclamationTriangle, FaDoorOpen, FaPrint, FaUsersCog, FaLock,
  FaBan, FaExchangeAlt, FaClock, FaTags, FaUndo, FaSyncAlt,
  FaAngleLeft, FaAngleRight, FaInfoCircle, FaSave, FaCamera,
  FaLongArrowAltRight, FaUser, FaAddressCard, FaBuilding, FaUserPlus,
  FaPlus, FaEdit, FaTools, FaBox, FaCalendarAlt, FaTimes
} from 'react-icons/fa';
import supabase from '../../supabaseClient';

// Função para formatar CPF com máscara
const formatCpf = (cpf) => {
  if (!cpf) return '';
  
  // Remove tudo que não for número
  const numericCpf = cpf.replace(/\D/g, '');
  
  // Aplica a máscara XXX.XXX.XXX-XX
  if (numericCpf.length <= 3) {
    return numericCpf;
  } else if (numericCpf.length <= 6) {
    return `${numericCpf.slice(0, 3)}.${numericCpf.slice(3)}`;
  } else if (numericCpf.length <= 9) {
    return `${numericCpf.slice(0, 3)}.${numericCpf.slice(3, 6)}.${numericCpf.slice(6)}`;
  } else {
    return `${numericCpf.slice(0, 3)}.${numericCpf.slice(3, 6)}.${numericCpf.slice(6, 9)}-${numericCpf.slice(9, 11)}`;
  }
};

// Função para remover máscara do CPF
const removeCpfMask = (cpf) => {
  return cpf.replace(/\D/g, '');
};

// Componente que representa visualmente uma pulseira
const WristbandPreview = ({ number, color, rotation = 0, scale = 1, onClick }) => {
  // Cor padrão da pulseira
  const wristbandColor = color || '#2196f3';
  
  const handleClick = () => {
    if (onClick) onClick();
  };
  
  return (
    <div 
      onClick={handleClick}
      style={{ 
        transform: `rotate(${rotation}deg) scale(${scale})`,
        transformOrigin: 'center',
        cursor: onClick ? 'pointer' : 'default',
        transition: 'transform 0.3s ease'
      }}
    >
      <div style={{
        width: '200px',
        height: '40px',
        backgroundColor: wristbandColor,
        borderRadius: '20px',
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '0 15px',
        boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
        overflow: 'hidden'
      }}>
        {/* Listras decorativas */}
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '8px',
          background: `linear-gradient(90deg, ${wristbandColor} 0%, ${wristbandColor} 100%)`,
          opacity: 0.7
        }} />
        
        {/* Número da pulseira */}
        <div style={{
          fontWeight: 'bold',
          fontSize: '18px',
          color: 'white',
          textShadow: '0 1px 2px rgba(0,0,0,0.1)',
          letterSpacing: '1px'
        }}>
          {number || 'DIGITE O NÚMERO'}
        </div>
      </div>
      
      {/* Trava da pulseira */}
      <div style={{
        width: '10px',
        height: '20px',
        backgroundColor: wristbandColor,
        borderBottomLeftRadius: '5px',
        borderBottomRightRadius: '5px',
        marginLeft: '95px',
        marginTop: '-2px'
      }} />
    </div>
  );
};

// Componente de leitor NFC/RFID
const NFCReader = ({ onScan, isActive }) => {
  return (
    <div className="text-center mb-4">
      <div 
        style={{
          width: '150px',
          height: '150px',
          margin: '0 auto',
          borderRadius: '50%',
          backgroundColor: isActive ? '#e8f5e9' : '#f5f5f5',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: isActive ? '0 0 15px rgba(76, 175, 80, 0.5)' : 'none',
          transition: 'all 0.3s ease',
          cursor: 'pointer',
          border: isActive ? '2px solid #4caf50' : '2px solid #e0e0e0'
        }}
        onClick={onScan}
      >
        <div style={{
          width: '80px',
          height: '80px',
          borderRadius: '50%',
          border: isActive ? '2px solid #4caf50' : '2px solid #bdbdbd',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'white'
        }}>
          <div style={{
            width: '50px',
            height: '50px',
            borderRadius: '50%',
            border: isActive ? '2px solid #4caf50' : '2px solid #e0e0e0',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <FaQrcode 
              size={24} 
              color={isActive ? '#4caf50' : '#bdbdbd'} 
            />
          </div>
        </div>
      </div>
      
      <p className={isActive ? 'text-success fw-bold' : 'text-muted'}>
        {isActive ? 'Aproxime a pulseira' : 'Clique para ativar o leitor'}
      </p>
      
      {isActive && (
        <div className="scanner-line" style={{
          width: '150px',
          height: '2px',
          backgroundColor: '#4caf50',
          margin: '0 auto',
          position: 'relative',
          animation: 'scanning 1.5s infinite',
        }} />
      )}
      
      <style jsx>{`
        @keyframes scanning {
          0% {
            transform: translateY(-30px);
            opacity: 0;
          }
          50% {
            opacity: 1;
          }
          100% {
            transform: translateY(30px);
            opacity: 0;
          }
        }
      `}</style>
    </div>
  );
};

// Componente para a timeline de histórico de pulseiras
const WristbandTimeline = ({ history }) => {
  if (!history || history.length === 0) {
    return (
      <Alert variant="info">
        <FaInfoCircle className="me-2" />
        Nenhum histórico de pulseira encontrado.
      </Alert>
    );
  }

  return (
    <div className="wristband-history">
      {history.map((item, index) => (
        <div 
          key={item.id || index} 
          className="history-item mb-3 p-3"
          style={{ 
            backgroundColor: item.isPrimeiroRegistro ? '#e8f5e9' : '#f8f9fa',
            borderRadius: '8px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
            borderLeft: item.isPrimeiroRegistro ? '4px solid #28a745' : '4px solid #2196f3'
          }}
        >
          <div className="d-flex justify-content-between mb-2">
            <div style={{ fontSize: '0.9rem', color: '#6c757d' }}>
              <FaClock className="me-1" /> {item.created_at ? new Date(item.created_at).toLocaleDateString() : ''} - {item.created_at ? new Date(item.created_at).toLocaleTimeString() : ''}
            </div>
            <Badge bg={item.isPrimeiroRegistro ? "success" : "secondary"}>
              <FaUser className="me-1" /> {item.registrado_por || 'Sistema'}
            </Badge>
          </div>
          
          <div className="d-flex align-items-center mb-2 gap-2">
            {item.isPrimeiroRegistro ? (
              <>
                <Badge bg="success" style={{ padding: '8px 12px', fontSize: '14px' }}>
                  <FaCheckCircle className="me-1" />
                  Primeira Entrega
                </Badge>
                <FaLongArrowAltRight style={{ color: '#6c757d' }} />
                <Badge bg="primary" style={{ padding: '8px 12px', fontSize: '14px' }}>
                  {item.pulseira_nova}
                </Badge>
              </>
            ) : (
              <>
                <Badge bg="warning" style={{ padding: '8px 12px', fontSize: '14px' }}>
                  {item.pulseira_anterior || 'Sem pulseira'}
                </Badge>
                <FaLongArrowAltRight style={{ color: '#6c757d' }} />
                <Badge bg="success" style={{ padding: '8px 12px', fontSize: '14px' }}>
                  {item.pulseira_nova}
                </Badge>
              </>
            )}
          </div>
          
          <div style={{ fontSize: '0.9rem' }}>
            <strong>Motivo:</strong> {item.motivo || 'Não informado'}
          </div>
        </div>
      ))}
    </div>
  );
};

// Componente de Modal de Histórico com visualização simplificada
const HistoryModal = ({ 
  showModal, 
  handleCloseModal, 
  selectedPerson, 
  personType,
  accessHistory, 
  wristbandHistory,
  formatDate 
}) => {
  const [activeTab, setActiveTab] = useState('access');

  // Obter histórico de acesso da pessoa
  const getPersonAccessHistory = () => {
    if (!selectedPerson || !accessHistory) return [];
    
    // Filtrar baseado no tipo de pessoa (montador ou credencial)
    if (personType === 'montador') {
      return accessHistory.filter(access => access.montador_id === selectedPerson.id);
    } else {
      return accessHistory.filter(access => access.credencial_id === selectedPerson.id);
    }
  };
  
  // Obter histórico completo de pulseiras da pessoa (incluindo primeiro registro)
  const getWristbandHistory = () => {
    if (!selectedPerson || !wristbandHistory || !accessHistory) return [];
    
    let historyData = [];
    
    // Filtrar histórico de trocas
    const trocas = wristbandHistory.filter(history => 
      personType === 'montador' 
        ? history.montador_id === selectedPerson.id
        : history.credencial_id === selectedPerson.id
    );
    
    // Adicionar trocas ao histórico
    historyData = [...trocas];
    
    // VERSÃO ORIGINAL: 
    // Buscar o primeiro registro de acesso com pulseira (entrada inicial)
    // const primeiroRegistro = accessHistory
    //   .filter(access => 
    //     personType === 'montador' 
    //       ? access.montador_id === selectedPerson.id
    //       : access.credencial_id === selectedPerson.id
    //   )
    //   .filter(access => access.pulseira_numero) // Apenas registros com pulseira
    //   .sort((a, b) => new Date(a.data_entrada) - new Date(b.data_entrada))[0]; // Primeiro registro
    
    // NOVA VERSÃO:
    // Encontrar a primeira pulseira com base nas trocas
    let primeiraPulseira = null;
    
    // Ordenar as trocas cronologicamente (da mais antiga para a mais recente)
    const trocasOrdenadas = [...trocas].sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
    
    if (trocasOrdenadas.length > 0) {
      // Se existem trocas, a primeira pulseira é a anterior da primeira troca
      // Se a primeira troca não tem pulseira anterior (null), então usamos a primeira pulseira nova
      const primeiratroca = trocasOrdenadas[0];
      primeiraPulseira = primeiratroca.pulseira_anterior || primeiratroca.pulseira_nova;
    } else {
      // Se não existem trocas, buscamos no histórico de acesso
      const primeiroRegistro = accessHistory
        .filter(access => 
          personType === 'montador' 
            ? access.montador_id === selectedPerson.id
            : access.credencial_id === selectedPerson.id
        )
        .filter(access => access.pulseira_numero) // Apenas registros com pulseira
        .sort((a, b) => new Date(a.data_entrada) - new Date(b.data_entrada))[0]; // Primeiro registro
      
      if (primeiroRegistro) {
        primeiraPulseira = primeiroRegistro.pulseira_numero;
      }
    }
    
    // Se encontramos uma primeira pulseira, adicionar o registro inicial
    if (primeiraPulseira) {
      // Encontrar a data mais antiga de registro (seja em acesso ou trocas)
      let dataInicial;
      
      if (trocasOrdenadas.length > 0) {
        // Verificar se o primeiro acesso é mais antigo que a primeira troca
        const primeiroAcesso = accessHistory
          .filter(access => 
            personType === 'montador' 
              ? access.montador_id === selectedPerson.id
              : access.credencial_id === selectedPerson.id
          )
          .filter(access => access.pulseira_numero) // Apenas registros com pulseira
          .sort((a, b) => new Date(a.data_entrada) - new Date(b.data_entrada))[0];
        
        if (primeiroAcesso && new Date(primeiroAcesso.data_entrada) < new Date(trocasOrdenadas[0].created_at)) {
          dataInicial = primeiroAcesso.data_entrada;
        } else {
          dataInicial = trocasOrdenadas[0].created_at;
        }
      } else {
        // Se não há trocas, usar a data do primeiro acesso
        const primeiroAcesso = accessHistory
          .filter(access => 
            personType === 'montador' 
              ? access.montador_id === selectedPerson.id
              : access.credencial_id === selectedPerson.id
          )
          .filter(access => access.pulseira_numero)
          .sort((a, b) => new Date(a.data_entrada) - new Date(b.data_entrada))[0];
        
        if (primeiroAcesso) {
          dataInicial = primeiroAcesso.data_entrada;
        } else {
          // Fallback: usar data atual se não há registros (não deveria acontecer)
          dataInicial = new Date().toISOString();
        }
      }
      
      // Adicionar o registro de primeira entrega no início do histórico
      historyData.unshift({
        id: `primeiro_pulseira_${primeiraPulseira}`,
        pulseira_anterior: null,
        pulseira_nova: primeiraPulseira,
        tipo_pulseira_anterior: null,
        tipo_pulseira_nova: null,
        motivo: 'Primeira entrega de pulseira',
        registrado_por: 'Sistema',
        created_at: dataInicial,
        isPrimeiroRegistro: true
      });
    }
    
    // Ordenar por data (mais recente primeiro)
    return historyData.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
  };

  // Estilos para o modal
  const modalStyles = {
    overlay: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 9999,
      padding: '1rem',
      backdropFilter: 'blur(5px)'
    },
    content: {
      position: 'relative',
      width: '100%',
      maxWidth: '750px',
      maxHeight: '90vh',
      display: 'flex',
      flexDirection: 'column',
      backgroundColor: 'white',
      borderRadius: '20px',
      boxShadow: '0 20px 60px rgba(0, 0, 0, 0.15)',
      border: '1px solid rgba(255, 255, 255, 0.2)',
      overflow: 'hidden',
      animationName: 'fadeInUp',
      animationDuration: '0.4s',
      animationTimingFunction: 'cubic-bezier(0.175, 0.885, 0.32, 1.275)',
      animationFillMode: 'both'
    },
    header: {
      background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
      borderBottom: '1px solid rgba(0, 0, 0, 0.05)',
      padding: '1.5rem 2rem',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center'
    },
    closeButton: {
      width: '36px',
      height: '36px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      borderRadius: '50%',
      border: 'none',
      background: 'white',
      color: '#6c757d',
      fontSize: '1rem',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)'
    },
    title: {
      fontSize: '1.25rem',
      fontWeight: '600',
      color: '#1a1a1a',
      margin: '0',
      display: 'flex',
      alignItems: 'center',
      gap: '0.75rem'
    },
    body: {
      padding: '2rem',
      overflowY: 'auto'
    },
    footer: {
      display: 'flex',
      justifyContent: 'space-between',
      padding: '1.5rem 2rem',
      borderTop: '1px solid rgba(0, 0, 0, 0.05)',
      background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)'
    }
  };

  if (!showModal || !selectedPerson) return null;

  return (
    <div style={modalStyles.overlay}>
      <div style={modalStyles.content}>
        <div style={modalStyles.header}>
          <h2 style={modalStyles.title}>
            <FaHistory style={{ color: '#6c757d' }} />
            Histórico Completo
          </h2>
          <button 
            onClick={handleCloseModal}
            style={modalStyles.closeButton}
            className="modal-close-btn"
            aria-label="Fechar"
          >
            ✕
          </button>
        </div>
        
        <div style={modalStyles.body}>
          {/* Informações da pessoa */}
          <div className="d-flex align-items-center mb-4 p-3 bg-light rounded">
            <div>
              <h4 className="mb-0">{selectedPerson.nome}</h4>
              <p className="text-muted mb-0">
                {selectedPerson.empresa || selectedPerson.empresa_montadora_nome} - {selectedPerson.cargo}
              </p>
              <div className="mt-1">
                <Badge bg={selectedPerson.status === 'ativo' ? 'success' : 'danger'} className="me-2">
                  {selectedPerson.status === 'ativo' ? 'Ativo' : 'Inativo'}
                </Badge>
                {personType === 'credencial' && (
                  <Badge bg="info" className="ms-2">
                    {selectedPerson.tipo_credencial}
                  </Badge>
                )}
              </div>
            </div>
          </div>

          {/* Tabs para alternar entre histórico de acesso e pulseiras */}
          <Tab.Container id="history-tabs" activeKey={activeTab} onSelect={setActiveTab}>
            <Nav variant="tabs" className="mb-4">
              <Nav.Item>
                <Nav.Link eventKey="access" className="d-flex align-items-center">
                  <FaUserCheck className="me-2" />
                  Histórico de Acesso
                </Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link eventKey="wristbands" className="d-flex align-items-center">
                  <FaTags className="me-2" />
                  Histórico de Pulseiras
                </Nav.Link>
              </Nav.Item>
            </Nav>
            
            <Tab.Content>
              <Tab.Pane eventKey="access">
                <h5 className="mb-3">Registros de Entrada e Saída</h5>
                {getPersonAccessHistory().length > 0 ? (
                  <div className="modern-table-container">
                    <table className="modern-table">
                      <thead>
                        <tr>
                          <th>Data</th>
                          <th>Hora</th>
                          <th>Tipo</th>
                          <th>Pulseira</th>
                          {/*<th>Registrado por</th>*/}
                        </tr>
                      </thead>
                      <tbody>
                        {getPersonAccessHistory().map(access => (
                          <tr key={access.id}>
                            <td>{formatDate(access.data_entrada)}</td>
                            <td>{access.data_entrada ? new Date(access.data_entrada).toLocaleTimeString() : ''}</td>
                            <td>
                              <Badge bg={!access.data_saida ? 'success' : 'warning'} className="modern-badge">
                                {!access.data_saida ? 'Entrada' : 'Saída'}
                              </Badge>
                            </td>
                            <td>
                              {access.pulseira_numero ? (
                                <Badge bg="info" className="modern-badge">
                                  {access.pulseira_numero}
                                </Badge>
                              ) : (
                                'Não registrada'
                              )}
                            </td>
                            {/*<td>{access.registrado_por || 'Sistema'}</td>*/}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <Alert variant="info">
                    <FaTimesCircle className="me-2" />
                    Nenhum registro de acesso encontrado.
                  </Alert>
                )}
              </Tab.Pane>
              
              <Tab.Pane eventKey="wristbands">
                <h5 className="mb-3">Histórico de Pulseiras</h5>
                <WristbandTimeline history={getWristbandHistory()} />
              </Tab.Pane>
            </Tab.Content>
          </Tab.Container>
        </div>
        
        <div style={modalStyles.footer}>
          <Button 
            variant="secondary"
            onClick={handleCloseModal}
          >
            Fechar
          </Button>
        </div>
      </div>
    </div>
  );
};

// Componente Principal
const ControleAcesso = () => {
  // Estados
  const [activeTab, setActiveTab] = useState('montadores');
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [hasSearched, setHasSearched] = useState(false);
  const [selectedPerson, setSelectedPerson] = useState(null);
  const [personType, setPersonType] = useState(null); // 'montador' ou 'credencial'
  const [showHistory, setShowHistory] = useState(false);
  const [filters, setFilters] = useState({
    tipoCredencial: ''
  });
  const [loading, setLoading] = useState(false);
  
  // Estado para o modal de pulseira
  const [showWristbandModal, setShowWristbandModal] = useState(false);
  const [wristbandData, setWristbandData] = useState({
    number: '',
    color: '#2196f3',
    isExchange: false,
    exchangeReason: ''
  });
  const [wristbandError, setWristbandError] = useState('');
  
  // Estado para gerenciar o passo do wizard
  const [wizardStep, setWizardStep] = useState(2);
  
  // Estado para o leitor NFC
  const [nfcReaderActive, setNfcReaderActive] = useState(false);
  
  // Estado para o modal de histórico
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  
  // Estados para dados do banco
  const [montadores, setMontadores] = useState([]);
  const [credenciais, setCredenciais] = useState([]);
  const [accessHistory, setAccessHistory] = useState([]);
  const [wristbandHistory, setWristbandHistory] = useState([]);
  const [empresasMontadoras, setEmpresasMontadoras] = useState([]);
  const [expositores, setExpositores] = useState([]);
  
  // Estado para pessoas presentes
  const [presentPeople, setPresentPeople] = useState([]);
  
  // NOVOS ESTADOS PARA TROCA DE MONTADOR
  const [showTrocaMontadorModal, setShowTrocaMontadorModal] = useState(false);
  const [montadorParaTroca, setMontadorParaTroca] = useState(null);
  const [activeTrocaTab, setActiveTrocaTab] = useState('dados');
  const [trocaFormData, setTrocaFormData] = useState({
    nome: '',
    cpf: '',
    empresa_montadora_id: '',
    evento_id: '',
    motivo: '',
    observacoes: ''
  });
  const [empresaMontadoraSelecionada, setEmpresaMontadoraSelecionada] = useState(null);
  const [expositorSelecionado, setExpositorSelecionado] = useState(null);
  const [eventosVinculados, setEventosVinculados] = useState([]);
  const [produtos, setProdutos] = useState([]);
  
  // Tipos de credencial disponíveis
  const tiposCredencial = ['Expositor', 'Staff', 'Imprensa', 'VIP', 'Convidado'];
  
  // Motivos de troca disponíveis
  const motivosTroca = [
    'Funcionário desligado',
    'Funcionário substituído',
    'Problemas de documentação',
    'Solicitação da empresa',
    'Outros motivos'
  ];
  
  // Efeito para carregar dados iniciais
  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        // Carregar empresas montadoras
        const { data: empresasData, error: empresasError } = await supabase
          .from('caex_empresas_montadoras')
          .select('*')
          .order('nome');
        
        if (empresasError) throw empresasError;
        setEmpresasMontadoras(empresasData || []);
        
        // Carregar expositores
        const { data: expositoresData, error: expositoresError } = await supabase
          .from('caex_expositores')
          .select('*')
          .order('nome_empresa');
        
        if (expositoresError) throw expositoresError;
        setExpositores(expositoresData || []);
        
        // Carregar produtos vinculados a montadores
        const { data: produtosData, error: produtosError } = await supabase
          .from('caex_produtos')
          .select('id, nome, descricao, preco, imagem_url')
          .eq('ativo', true)
          .eq('vinculado_montador', true)
          .order('nome');
        
        if (produtosError) throw produtosError;
        setProdutos(produtosData || []);
        
        // Carregar dados de pessoas presentes
        fetchPresentPeople();
      } catch (error) {
        console.error('Erro ao carregar dados iniciais:', error);
      }
    };
    
    fetchInitialData();
  }, []);
  
  // Função para obter a pulseira atual da pessoa
  const getCurrentWristband = async (personId, type) => {
    try {
      // Buscar o registro de acesso mais recente que tenha pulseira
      let query = supabase
        .from('caex_registros_acesso')
        .select('pulseira_numero')
        .order('data_entrada', { ascending: false })
        .limit(1);
      
      if (type === 'montador') {
        query = query.eq('montador_id', personId);
      } else {
        query = query.eq('credencial_id', personId);
      }
      
      const { data, error } = await query;
      
      if (error) throw error;
      
      if (data && data.length > 0 && data[0].pulseira_numero) {
        return {
          number: data[0].pulseira_numero
        };
      }
      
      return null;
    } catch (error) {
      console.error('Erro ao buscar pulseira atual:', error);
      return null;
    }
  };
  
  // Função para buscar pessoas presentes hoje
  const fetchPresentPeople = async () => {
    try {
      setLoading(true);
      
      const today = new Date().toISOString().split('T')[0]; // Formato YYYY-MM-DD
      
      // Buscar montadores presentes
      const { data: montadoresPresentes, error: montadoresError } = await supabase
        .from('caex_registros_acesso')
        .select(`
          montador_id,
          montadores:montador_id (
            id,
            nome,
            cpf,
            empresa_montadora_id,
            caex_empresas_montadoras (
              id,
              nome
            )
          ),
          pulseira_numero
        `)
        .is('data_saida', null) // Apenas os que não têm saída registrada
        .not('montador_id', 'is', null)
        .gte('data_entrada', `${today}T00:00:00`) // Entradas de hoje
        .lt('data_entrada', `${today}T23:59:59`);
      
      if (montadoresError) throw montadoresError;
      
      // Buscar credenciais presentes
      const { data: credenciaisPresentes, error: credenciaisError } = await supabase
        .from('caex_registros_acesso')
        .select(`
          credencial_id,
          credenciais:credencial_id (
            id,
            nome,
            cpf,
            empresa,
            tipo_credencial
          ),
          pulseira_numero
        `)
        .is('data_saida', null) // Apenas os que não têm saída registrada
        .not('credencial_id', 'is', null) // Apenas os registros com credencial_id preenchido
        .gte('data_entrada', `${today}T00:00:00`) // Entradas de hoje
        .lt('data_entrada', `${today}T23:59:59`);
      
      if (credenciaisError) throw credenciaisError;
      
      // Processar montadores presentes
      const montadoresList = montadoresPresentes
        .filter(registro => registro.montadores) // Filtrar registros válidos
        .map(registro => ({
          id: registro.montadores.id,
          nome: registro.montadores.nome,
          cpf: formatCpf(registro.montadores.cpf), // APLICAR MÁSCARA DE CPF
          empresa_montadora_id: registro.montadores.empresa_montadora_id,
          empresa_montadora_nome: registro.montadores.caex_empresas_montadoras?.nome || 'Sem empresa',
          pulseira: registro.pulseira_numero,
          type: 'montador'
        }));
      
      // Processar credenciais presentes
      const credenciaisList = credenciaisPresentes
        .filter(registro => registro.credenciais) // Filtrar registros válidos
        .map(registro => ({
          id: registro.credenciais.id,
          nome: registro.credenciais.nome,
          cpf: formatCpf(registro.credenciais.cpf), // APLICAR MÁSCARA DE CPF
          empresa: registro.credenciais.empresa,
          tipo_credencial: registro.credenciais.tipo_credencial,
          pulseira: registro.pulseira_numero,
          type: 'credencial'
        }));
      
      // Combinar as duas listas
      setPresentPeople([...montadoresList, ...credenciaisList]);
      
    } catch (error) {
      console.error('Erro ao buscar pessoas presentes:', error);
    } finally {
      setLoading(false);
    }
  };
  
  // Função para buscar montadores
  // Função atualizada para buscar montadores com informações do pedido
  const fetchMontadores = async (searchTerm = '') => {
    setLoading(true);
    try {
      let query = supabase
        .from('caex_montadores')
        .select(`
          *,
          caex_empresas_montadoras (
            id,
            nome
          ),
          caex_pedidos (
            id,
            status,
            data_pedido,
            expositor_id,
            evento_id
          )
        `);
      
      if (searchTerm) {
        if (searchTerm.match(/^\d/)) {
          // Se o termo começa com dígito, assume que é um CPF
          // Remove máscara para buscar no banco
          const cpfLimpo = removeCpfMask(searchTerm);
          query = query.ilike('cpf', `%${cpfLimpo}%`);
        } else {
          // Senão, busca por nome
          query = query.ilike('nome', `%${searchTerm}%`);
        }
      }
      
      // Ordenação
      query = query.order('id', { ascending: false });
      
      const { data, error } = await query;
      
      if (error) {
        console.error('Erro ao buscar montadores:', error);
        throw error;
      }
      
      // Formatar os dados para incluir informações de empresa e pedido
      const montadoresComEmpresa = data.map(montador => {
        return {
          ...montador,
          cpf: formatCpf(montador.cpf), // APLICAR MÁSCARA DE CPF
          empresa_montadora_nome: montador.caex_empresas_montadoras?.nome || 'Sem empresa',
          pedido_status: montador.caex_pedidos?.status || null,
          pedido_id: montador.caex_pedidos?.id || null,
          data_pedido: montador.caex_pedidos?.data_pedido || null
        };
      });
      
      // Para cada montador, obter a pulseira atual
      const montadoresComPulseira = await Promise.all(
        montadoresComEmpresa.map(async (montador) => {
          const wristband = await getCurrentWristband(montador.id, 'montador');
          return {
            ...montador,
            pulseira: wristband ? wristband.number : null
          };
        })
      );
      
      return montadoresComPulseira;
    } catch (error) {
      console.error('Erro ao buscar montadores:', error);
      return [];
    } finally {
      setLoading(false);
    }
  };
  
  // Função para buscar credenciais
  const fetchCredenciais = async (searchTerm = '', tipoCredencial = '') => {
    setLoading(true);
    try {
      let query = supabase
        .from('caex_credenciais')
        .select(`
          *,
          caex_pedidos (
            id,
            status,
            data_pedido
          )
        `);
      
      if (searchTerm) {
        if (searchTerm.match(/^\d/)) {
          // Se o termo começa com dígito, assume que é um CPF
          // Remove máscara para buscar no banco
          const cpfLimpo = removeCpfMask(searchTerm);
          query = query.ilike('cpf', `%${cpfLimpo}%`);
        } else {
          // Senão, busca por nome
          query = query.ilike('nome', `%${searchTerm}%`);
        }
      }
      
      if (tipoCredencial) {
        query = query.eq('tipo_credencial', tipoCredencial);
      }
      
      query = query.order('id', { ascending: false });
      
      const { data, error } = await query;
      
      if (error) {
        console.error('Erro ao buscar credenciais:', error);
        throw error;
      }
      
      // Adicionar nome do expositor para cada credencial
      const credenciaisComExpositor = data.map(credencial => {
        const expositor = expositores.find(e => e.id === credencial.expositor_id);
        return {
          ...credencial,
          cpf: formatCpf(credencial.cpf), // APLICAR MÁSCARA DE CPF
          expositor_nome: expositor ? expositor.nome_empresa : 'Expositor não encontrado',
          pedido_status: credencial.caex_pedidos?.status || null,
          pedido_id: credencial.caex_pedidos?.id || null,
          data_pedido: credencial.caex_pedidos?.data_pedido || null
        };
      });
      
      // Para cada credencial, obter a pulseira atual
      const credenciaisComPulseira = await Promise.all(
        credenciaisComExpositor.map(async (credencial) => {
          const wristband = await getCurrentWristband(credencial.id, 'credencial');
          return {
            ...credencial,
            pulseira: wristband ? wristband.number : null
          };
        })
      );
      
      return credenciaisComPulseira;
    } catch (error) {
      console.error('Erro ao buscar credenciais:', error);
      return [];
    } finally {
      setLoading(false);
    }
  };

  // NOVA FUNÇÃO: Verificar se a pessoa pode acessar considerando o status do pedido
const canAccessToday = (person) => {
  // 1. Verificar se o status da pessoa é ativo
  if (person.status !== 'ativo') {
    return {
      canAccess: false,
      reason: 'Status inativo'
    };
  }
  
  // 2. Para montadores, verificar se o pedido está aprovado
  if (personType === 'montador' || person.empresa_montadora_nome) {
    if (!person.pedido_id) {
      return {
        canAccess: false,
        reason: 'Montador não vinculado a nenhum pedido'
      };
    }
    
    if (person.pedido_status !== 'aprovado') {
      return {
        canAccess: false,
        reason: `Pedido ${person.pedido_status || 'sem status'} (ID: ${person.pedido_id})`
      };
    }
  }
  
  // 3. Para credenciais, verificar regras específicas (se aplicável)
  if (personType === 'credencial' || person.tipo_credencial) {
    // Se credenciais também tiverem pedidos vinculados, verificar aqui
    if (person.pedido_id && person.pedido_status !== 'aprovado') {
      return {
        canAccess: false,
        reason: `Pedido ${person.pedido_status || 'sem status'} (ID: ${person.pedido_id})`
      };
    }
  }
  
  return {
    canAccess: true,
    reason: 'Autorizado'
  };
};
  
  // Função para buscar histórico de acesso
  const fetchAccessHistory = async (personId, type) => {
    try {
      let query = supabase.from('caex_registros_acesso').select('*');
      
      if (type === 'montador') {
        query = query.eq('montador_id', personId);
      } else {
        query = query.eq('credencial_id', personId);
      }
      
      query = query.order('data_entrada', { ascending: false });
      
      const { data, error } = await query;
      
      if (error) throw error;
      
      return data || [];
    } catch (error) {
      console.error('Erro ao buscar histórico de acesso:', error);
      return [];
    }
  };
  
  // Função para buscar histórico de pulseiras
  const fetchWristbandHistory = async (personId, type) => {
    try {
      console.log(`Buscando histórico de pulseiras para ${type} ID: ${personId}`);
      
      let query = supabase.from('caex_historico_pulseiras').select('*');
      
      if (type === 'montador') {
        query = query.eq('montador_id', personId);
      } else {
        query = query.eq('credencial_id', personId);
      }
      
      query = query.order('created_at', { ascending: false });
      
      const { data, error } = await query;
      
      if (error) {
        console.error('Erro na consulta de histórico de pulseiras:', error);
        throw error;
      }
      
      console.log('Histórico de pulseiras encontrado:', data);
      return data || [];
    } catch (error) {
      console.error('Erro ao buscar histórico de pulseiras:', error);
      return [];
    }
  };

  // NOVAS FUNÇÕES PARA TROCA DE MONTADOR
  
  // Função para buscar eventos vinculados ao expositor da empresa montadora
  const fetchEventosVinculadosAoExpositor = async (expositorId) => {
    try {
      console.log('Buscando eventos vinculados ao expositor:', expositorId);
      
      const { data, error } = await supabase
        .from('caex_expositor_evento')
        .select(`
          evento_id,
          caex_eventos (
            id,
            nome,
            descricao,
            data_inicio,
            data_fim,
            local,
            status
          )
        `)
        .eq('expositor_id', expositorId);
      
      if (error) throw error;
      
      const eventosData = data?.map(item => item.caex_eventos).filter(evento => evento) || [];
      
      console.log('Eventos vinculados encontrados:', eventosData);
      setEventosVinculados(eventosData);
      
    } catch (error) {
      console.error('Erro ao buscar eventos vinculados:', error);
      setEventosVinculados([]);
    }
  };

  // Handler para abrir modal de troca de montador
  const handleTrocarMontador = (montador) => {
    setMontadorParaTroca(montador);
    setEmpresaMontadoraSelecionada(null);
    setExpositorSelecionado(null);
    setEventosVinculados([]);
    setTrocaFormData({
      nome: '',
      cpf: '',
      empresa_montadora_id: montador.empresa_montadora_id?.toString() || '',
      evento_id: montador.evento_id?.toString() || '',
      motivo: '',
      observacoes: ''
    });
    setActiveTrocaTab('dados');
    setShowTrocaMontadorModal(true);
    
    // Se já tem empresa montadora selecionada, buscar dados relacionados
    if (montador.empresa_montadora_id) {
      const empresa = empresasMontadoras.find(emp => emp.id === montador.empresa_montadora_id);
      if (empresa) {
        setEmpresaMontadoraSelecionada(empresa);
        if (empresa.expositor_id) {
          fetchEventosVinculadosAoExpositor(empresa.expositor_id);
          const expositor = expositores.find(exp => exp.id === empresa.expositor_id);
          setExpositorSelecionado(expositor || null);
        }
      }
    }
  };

  // Handler para mudança de empresa montadora na troca
  const handleTrocaEmpresaMontadoraChange = async (e) => {
    const empresaId = e.target.value;
    
    if (empresaId) {
      const empresa = empresasMontadoras.find(emp => emp.id === parseInt(empresaId));
      setEmpresaMontadoraSelecionada(empresa);
      
      // Atualizar formulário
      setTrocaFormData({
        ...trocaFormData,
        empresa_montadora_id: empresaId
      });
      
      // Se a empresa tem expositor vinculado, buscar eventos desse expositor
      if (empresa && empresa.expositor_id) {
        await fetchEventosVinculadosAoExpositor(empresa.expositor_id);
        
        // Buscar dados do expositor
        const expositor = expositores.find(exp => exp.id === empresa.expositor_id);
        setExpositorSelecionado(expositor || null);
      } else {
        // Se não tem expositor vinculado, limpar eventos
        setEventosVinculados([]);
        setExpositorSelecionado(null);
      }
    } else {
      setEmpresaMontadoraSelecionada(null);
      setExpositorSelecionado(null);
      setEventosVinculados([]);
      setTrocaFormData({
        ...trocaFormData,
        empresa_montadora_id: ''
      });
    }
  };

  // Handler para mudanças no formulário de troca
  const handleTrocaChange = (e) => {
    const { name, value } = e.target;
    setTrocaFormData({
      ...trocaFormData,
      [name]: value
    });
  };

  // Handler para click nas tabs da troca
  const handleTrocaTabClick = (tab) => {
    setActiveTrocaTab(tab);
  };

  // Função para submeter a troca de montador
  const handleSubmitTrocaMontador = async (e) => {
    if (e) e.preventDefault();
    
    if (!trocaFormData.nome || !trocaFormData.cpf || !trocaFormData.empresa_montadora_id) {
      alert('Por favor, preencha todos os campos obrigatórios.');
      return;
    }

    if (!trocaFormData.evento_id) {
      alert('Por favor, selecione um evento para vincular este montador.');
      return;
    }

    if (!trocaFormData.motivo) {
      alert('Por favor, selecione um motivo para a troca.');
      return;
    }

    // NOVA VALIDAÇÃO - Verificar se o montador antigo tem pedido vinculado
    if (!montadorParaTroca.pedido_id) {
      // eslint-disable-next-line no-restricted-globals
      const confirmar = confirm('ATENÇÃO: O montador atual não possui um pedido vinculado.\n\nO novo montador também ficará sem pedido vinculado.\n\nDeseja continuar mesmo assim?');
      if (!confirmar) {
        return;
      }
    }

    try {
      setLoading(true);

      // 1. Inativar o montador antigo
      const { error: inativarError } = await supabase
        .from('caex_montadores')
        .update({ 
          status: 'inativo',
          updated_at: new Date()
        })
        .eq('id', montadorParaTroca.id);
      
      if (inativarError) throw inativarError;

      // 2. Criar o novo montador
      const novoMontadorData = {
        nome: trocaFormData.nome,
        cpf: trocaFormData.cpf,
        empresa_montadora_id: parseInt(trocaFormData.empresa_montadora_id),
        evento_id: parseInt(trocaFormData.evento_id),
        status: 'ativo',
        created_at: new Date(),
        updated_at: new Date()
      };

      // NOVO - Herda o pedido_id do montador antigo (se existir)
      if (montadorParaTroca.pedido_id) {
        novoMontadorData.pedido_id = montadorParaTroca.pedido_id;
      }

      const { data: novoMontador, error: criarError } = await supabase
        .from('caex_montadores')
        .insert([novoMontadorData])
        .select()
        .single();
      
      if (criarError) throw criarError;

      // 3. Registrar a substituição na tabela de substituições
      const substituicaoData = {
        montador_antigo_id: montadorParaTroca.id,
        montador_novo_id: novoMontador.id,
        motivo: trocaFormData.motivo,
        observacoes: trocaFormData.observacoes || null,
        registrado_por: 'Sistema', // Em um sistema real, pegar o usuário logado
        evento_id: parseInt(trocaFormData.evento_id),
        empresa_montadora_id: parseInt(trocaFormData.empresa_montadora_id)
      };

      const { error: substituicaoError } = await supabase
        .from('caex_montadores_substituicoes')
        .insert([substituicaoData]);
      
      if (substituicaoError) throw substituicaoError;

      alert(`Troca de montador realizada com sucesso!\nMontador anterior: ${montadorParaTroca.nome} (Inativo)\nNovo montador: ${novoMontador.nome} (Ativo)`);
      
      // Fechar modal e limpar estados
      setShowTrocaMontadorModal(false);
      setMontadorParaTroca(null);
      setTrocaFormData({
        nome: '',
        cpf: '',
        empresa_montadora_id: '',
        evento_id: '',
        motivo: '',
        observacoes: ''
      });

      // Atualizar listas
      await fetchPresentPeople();
      
      // Se há resultados de busca, atualizar
      if (hasSearched && searchTerm) {
        if (activeTab === 'montadores') {
          const results = await fetchMontadores(searchTerm);
          setSearchResults(results);
        }
      }

    } catch (error) {
      console.error('Erro ao realizar troca de montador:', error);
      alert(`Erro ao realizar troca de montador: ${error.message || 'Erro desconhecido'}`);
    } finally {
      setLoading(false);
    }
  };

  // Renderizar campo de empresa montadora para troca
  const renderTrocaEmpresaMontadoraField = () => {
    if (empresasMontadoras.length === 0) {
      return (
        <div style={{
          padding: '1rem',
          textAlign: 'center',
          color: '#6c757d',
          backgroundColor: '#f8f9fa',
          border: '1px solid #dee2e6',
          borderRadius: '8px'
        }}>
          <FaBuilding style={{ marginBottom: '0.5rem', fontSize: '1.5rem' }} />
          <div>Nenhuma empresa montadora disponível</div>
        </div>
      );
    }

    return (
      <div>
        <select
          id="empresa_montadora_id"
          name="empresa_montadora_id"
          value={trocaFormData.empresa_montadora_id}
          onChange={handleTrocaEmpresaMontadoraChange}
          className="form-input"
          required
        >
          <option value="">Selecione a empresa montadora</option>
          {empresasMontadoras.map(empresa => (
            <option key={empresa.id} value={empresa.id}>
              {empresa.nome} - {empresa.cnpj}
            </option>
          ))}
        </select>
        
        {empresaMontadoraSelecionada && (
          <div className="empresa-info mt-2">
            <div className="empresa-card">
              <FaBuilding className="empresa-icon" />
              <div>
                <div style={{ fontWeight: '600' }}>{empresaMontadoraSelecionada.nome}</div>
                <small>CNPJ: {empresaMontadoraSelecionada.cnpj}</small>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  // Renderizar dropdown de eventos para troca
  const renderTrocaEventosDropdown = () => {
    if (eventosVinculados.length === 0) {
      return (
        <div style={{
          padding: '1rem',
          textAlign: 'center',
          color: '#6c757d',
          backgroundColor: '#f8f9fa',
          border: '1px solid #dee2e6',
          borderRadius: '8px'
        }}>
          <FaCalendarAlt style={{ marginBottom: '0.5rem', fontSize: '1.5rem' }} />
          <div>Nenhum evento vinculado</div>
          <small>Selecione uma empresa montadora primeiro</small>
        </div>
      );
    }

    return (
      <select
        id="evento_id"
        name="evento_id"
        value={trocaFormData.evento_id}
        onChange={handleTrocaChange}
        className="form-input"
        required
      >
        <option value="">Selecione o evento</option>
        {eventosVinculados.map(evento => {
          const dataInicio = new Date(evento.data_inicio).toLocaleDateString('pt-BR');
          const dataFim = new Date(evento.data_fim).toLocaleDateString('pt-BR');
          const eventoLabel = `${evento.nome} (${dataInicio} - ${dataFim})`;
          
          return (
            <option key={evento.id} value={evento.id}>
              {eventoLabel}
            </option>
          );
        })}
      </select>
    );
  };

  // Renderizar produtos dropdown para troca
  const renderTrocaProdutosDropdown = () => {
    if (produtos.length === 0) {
      return (
        <div style={{
          padding: '1rem',
          textAlign: 'center',
          color: '#6c757d',
          backgroundColor: '#f8f9fa',
          border: '1px solid #dee2e6',
          borderRadius: '8px'
        }}>
          <FaBox style={{ marginBottom: '0.5rem', fontSize: '1.5rem' }} />
          <div>Nenhum produto disponível para montadores</div>
        </div>
      );
    }

    return (
      <div>
        <div style={{ marginBottom: '1rem', fontSize: '0.9rem', color: '#6c757d' }}>
          Produtos vinculados ao montador:
        </div>
        
        {produtos.map(produto => (
          <div
            key={produto.id}
            className="produto-card selected"
          >
            <div style={{ marginRight: '0.75rem', display: 'flex', alignItems: 'center' }}>
              <input
                type="checkbox"
                checked={true}
                disabled={true}
                style={{
                  width: '18px',
                  height: '18px',
                  accentColor: '#28a745',
                  cursor: 'not-allowed'
                }}
                readOnly
              />
            </div>
            
            <div className="produto-image">
              {produto.imagem_url ? (
                <img 
                  src={produto.imagem_url} 
                  alt={produto.nome}
                  style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '6px' }}
                />
              ) : (
                <FaBox style={{ color: '#6c757d', fontSize: '1.2rem' }} />
              )}
            </div>
            
            <div className="produto-info">
              <div className="produto-nome">{produto.nome}</div>
            </div>
          </div>
        ))}
        
        <div style={{
          marginTop: '1rem',
          padding: '0.75rem',
          backgroundColor: '#d1ecf1',
          color: '#0c5460',
          border: '1px solid #bee5eb',
          borderRadius: '8px',
          fontSize: '0.85rem',
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem'
        }}>
          <FaCheckCircle />
          Todos os produtos estão automaticamente vinculados aos montadores
        </div>
      </div>
    );
  };
  
  // Handlers originais
  const handleSearch = async () => {
    if (!searchTerm.trim()) {
      setSearchResults([]);
      setHasSearched(false);
      return;
    }
    
    let results = [];
    
    if (activeTab === 'montadores') {
      results = await fetchMontadores(searchTerm);
    } else {
      // Buscar credenciais
      results = await fetchCredenciais(searchTerm, filters.tipoCredencial);
    }
    
    setSearchResults(results);
    setHasSearched(true);
  };

  // Handler para alterações nos filtros
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters({
      ...filters,
      [name]: value
    });
  };

  // Handler para selecionar montador ou credencial
  const handleSelectPerson = async (person, type) => {
    // Buscar histórico de acesso e pulseiras
    const accessHistoryData = await fetchAccessHistory(person.id, type);
    const wristbandHistoryData = await fetchWristbandHistory(person.id, type);
    
    setAccessHistory(accessHistoryData);
    setWristbandHistory(wristbandHistoryData);
    
    setSelectedPerson(person);
    setPersonType(type);
    setShowHistory(false);
  };

  // Modificada para abrir o modal de histórico
  const handleShowHistory = async (person, type) => {
    // Buscar histórico de acesso e pulseiras
    const accessHistoryData = await fetchAccessHistory(person.id, type);
    const wristbandHistoryData = await fetchWristbandHistory(person.id, type);
    
    setAccessHistory(accessHistoryData);
    setWristbandHistory(wristbandHistoryData);
    
    setSelectedPerson(person);
    setPersonType(type);
    setShowHistoryModal(true);
  };

  // Função para fechar o modal de histórico
  const handleCloseHistoryModal = () => {
    setShowHistoryModal(false);
  };

  // Registrar entrada ou saída
  const handleRegisterAccess = async (type) => {
    try {
      const now = new Date();
      
      // Verificar se já existe um registro de entrada sem saída para este montador/credencial
      let query = supabase.from('caex_registros_acesso')
        .select('*')
        .is('data_saida', null);
      
      if (personType === 'montador') {
        query = query.eq('montador_id', selectedPerson.id);
      } else {
        query = query.eq('credencial_id', selectedPerson.id);
      }
      
      const { data: existingEntries, error: queryError } = await query;
      
      if (queryError) throw queryError;
      
      const existingEntry = existingEntries && existingEntries.length > 0 ? existingEntries[0] : null;
      
      if (type === 'entrada') {
        // Se já existe entrada sem saída, alertar
        if (existingEntry) {
          alert(`${selectedPerson.nome} já possui uma entrada registrada sem saída correspondente.`);
          return;
        }
        
        // Criar novo registro de entrada
        const data = {
          montador_id: personType === 'montador' ? selectedPerson.id : null,
          credencial_id: personType === 'credencial' ? selectedPerson.id : null,
          data_entrada: now.toISOString(),
          status_entrada: 'normal',
          pulseira_numero: selectedPerson.pulseira || null,
          registrado_por: 'Usuário Sistema' // Em um sistema real, pegar o usuário logado
        };
        
        const { error } = await supabase.from('caex_registros_acesso').insert([data]);
        
        if (error) throw error;
        
        alert(`Entrada registrada com sucesso para ${selectedPerson.nome}`);
        
        // Atualizar lista de pessoas presentes
        fetchPresentPeople();
      } else {
        // Registrar saída
        if (!existingEntry) {
          alert(`Não foi encontrado registro de entrada para ${selectedPerson.nome}.`);
          return;
        }
        
        // Atualizar registro existente com data de saída
        const { error } = await supabase
          .from('caex_registros_acesso')
          .update({ data_saida: now.toISOString() })
          .eq('id', existingEntry.id);
        
        if (error) throw error;
        
        alert(`Saída registrada com sucesso para ${selectedPerson.nome}`);
        
        // Atualizar lista de pessoas presentes
        fetchPresentPeople();
      }
      
      // Limpar seleção após o registro
      setSelectedPerson(null);
      setPersonType(null);
      
    } catch (error) {
      console.error('Erro ao registrar acesso:', error);
      alert('Erro ao registrar acesso. Por favor, tente novamente.');
    }
  };

  // Handler para abrir o modal de pulseira
  const handleOpenWristbandModal = () => {
    // Iniciar com campos vazios para o atendente digitar as informações
    setWristbandData({
      number: '',
      color: '#2196f3',
      isExchange: selectedPerson.pulseira ? true : false,
      exchangeReason: ''
    });
    
    setWristbandError('');
    setWizardStep(2);
    setShowWristbandModal(true);
  };

  // Handler para alterações no formulário de pulseira
  const handleWristbandChange = (e) => {
    const { name, value, type, checked } = e.target;
    setWristbandData({
      ...wristbandData,
      [name]: type === 'checkbox' ? checked : value
    });
    
    // Limpar erro quando o usuário corrige o número da pulseira
    if (name === 'number' && wristbandError) {
      setWristbandError('');
    }
  };

  // Handler para salvar a pulseira
  const handleSaveWristband = async () => {
    // Validar número da pulseira
    if (!wristbandData.number.trim()) {
      setWristbandError('Número da pulseira é obrigatório');
      return;
    }
    
    // Validar motivo de troca se for uma troca
    if (wristbandData.isExchange && !wristbandData.exchangeReason) {
      setWristbandError('Selecione o motivo da troca');
      return;
    }
    
    try {
      // Verificar se a pulseira já está em uso em registros ativos (sem data_saida)
      const { data: registrosAtivos, error: erroRegistros } = await supabase
        .from('caex_registros_acesso')
        .select('id, montador_id, credencial_id')
        .eq('pulseira_numero', wristbandData.number)
        .is('data_saida', null);
      
      if (erroRegistros) {
        console.error('Erro ao verificar pulseira em registros ativos:', erroRegistros);
        throw erroRegistros;
      }
      
      // Filtrar para não incluir a própria pessoa selecionada
      const emUsoPorOutraPessoa = registrosAtivos.filter(registro => {
        if (personType === 'montador') {
          return registro.montador_id !== selectedPerson.id;
        } else {
          return registro.credencial_id !== selectedPerson.id;
        }
      });
      
      if (emUsoPorOutraPessoa.length > 0) {
        setWristbandError('Este número de pulseira já está em uso por outra pessoa');
        return;
      }
      
      console.log('Salvando pulseira:', {
        personType,
        personId: selectedPerson.id,
        pulseira: wristbandData.number
      });
      
      // Obter a pulseira atual da pessoa
      const pulseirAtual = await getCurrentWristband(selectedPerson.id, personType);
      
      // Atualizar ou criar um novo registro de acesso com a pulseira
      const now = new Date();
      
      // Primeiro verificar se já existe um registro de entrada sem saída
      let query = supabase.from('caex_registros_acesso')
        .select('*')
        .is('data_saida', null);
      
      if (personType === 'montador') {
        query = query.eq('montador_id', selectedPerson.id);
      } else {
        query = query.eq('credencial_id', selectedPerson.id);
      }
      
      const { data: registrosExistentes, error: erroConsulta } = await query;
      
      if (erroConsulta) {
        console.error('Erro ao verificar registros existentes:', erroConsulta);
        throw erroConsulta;
      }
      
      if (registrosExistentes && registrosExistentes.length > 0) {
        // Atualizar o registro existente com a nova pulseira
        const registroExistente = registrosExistentes[0];
        
        const { error: erroUpdate } = await supabase
          .from('caex_registros_acesso')
          .update({
            pulseira_numero: wristbandData.number
          })
          .eq('id', registroExistente.id);
        
        if (erroUpdate) {
          console.error('Erro ao atualizar registro com nova pulseira:', erroUpdate);
          throw erroUpdate;
        }
      } else {
        // Criar um novo registro de acesso com a pulseira
        const novoRegistro = {
          montador_id: personType === 'montador' ? selectedPerson.id : null,
          credencial_id: personType === 'credencial' ? selectedPerson.id : null,
          data_entrada: now.toISOString(),
          status_entrada: 'normal',
          pulseira_numero: wristbandData.number,
          registrado_por: 'Usuário Sistema'
        };
        
        const { error: erroInsert } = await supabase
          .from('caex_registros_acesso')
          .insert([novoRegistro]);
        
        if (erroInsert) {
          console.error('Erro ao criar novo registro com pulseira:', erroInsert);
          throw erroInsert;
        }
      }
      
      // Se for uma troca, registrar no histórico
      if (wristbandData.isExchange) {
        // Criar dados para o histórico
        const historyData = {
          montador_id: personType === 'montador' ? selectedPerson.id : null,
          credencial_id: personType === 'credencial' ? selectedPerson.id : null,
          pulseira_anterior: pulseirAtual ? pulseirAtual.number : null,
          pulseira_nova: wristbandData.number,
          motivo: wristbandData.exchangeReason,
          registrado_por: 'Usuário Sistema'
        };
        
        console.log('Salvando histórico de pulseira:', historyData);
        
        const { error } = await supabase
          .from('caex_historico_pulseiras')
          .insert([historyData]);
        
        if (error) {
          console.error('Erro ao registrar histórico de pulseira:', error);
          throw error;
        }
      }
      
      // Atualizar a pessoa selecionada com a nova pulseira
      setSelectedPerson({
        ...selectedPerson,
        pulseira: wristbandData.number
      });
      
      // Atualizar dados
      await fetchPresentPeople();
      
      // Simular animação de sucesso
      setWizardStep(4);
      
      // Fechar o modal após 2 segundos
      setTimeout(() => {
        setShowWristbandModal(false);
        // Exibir mensagem de sucesso
        const successMessage = wristbandData.isExchange 
          ? `Troca de pulseira realizada com sucesso (${wristbandData.number})`
          : `Pulseira ${wristbandData.number} entregue com sucesso`;
        alert(successMessage);
      }, 2000);
      
    } catch (error) {
      console.error('Erro ao salvar pulseira:', error);
      setWristbandError(`Erro ao salvar pulseira: ${error.message || 'Erro desconhecido'}`);
    }
  };

  // Funções de renderização
  // Função atualizada para renderizar o status considerando o pedido
  const renderStatusBadge = (person) => {
    const accessInfo = canAccessToday(person);
    
    if (!accessInfo.canAccess) {
      return (
        <div>
          <Badge bg="danger" className="modern-badge d-block mb-1">
            {person.status === 'ativo' ? 'Bloqueado' : 'Inativo'}
          </Badge>
          <small className="text-danger">{accessInfo.reason}</small>
        </div>
      );
    }
    
    switch (person.status) {
      case 'ativo':
        return (
          <div>
            <Badge bg="success" className="modern-badge d-block mb-1">Autorizado</Badge>
            {person.pedido_status && (
              <small className="text-success">
                Pedido: {person.pedido_status} (#{person.pedido_id})
              </small>
            )}
          </div>
        );
      case 'inativo':
        return <Badge bg="danger" className="modern-badge">Inativo</Badge>;
      default:
        return <Badge bg="secondary" className="modern-badge">Indefinido</Badge>;
    }
  };

  // Formatar data
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('pt-BR');
  };

  // Renderizar o passo atual do wizard
  const renderWristbandRegistrationForm = () => (
    <div className="wizard-step">
      <h5 className="mb-3">Registrar Pulseira</h5>
      
      <div className="mb-4">
        {/* Instruções simples */}
        <Alert variant="info" className="mb-4">
          <FaInfoCircle className="me-2" />
          Digite o número da pulseira que será entregue {personType === 'montador' ? 'ao montador' : 'à pessoa'}.
        </Alert>
        
        {/* Checkbox para indicar se é uma troca */}
        <Form.Group className="mb-3">
          <Form.Check
            type="checkbox"
            id="exchange-checkbox"
            name="isExchange"
            checked={wristbandData.isExchange}
            onChange={handleWristbandChange}
            label={<span><FaExchangeAlt className="me-2" />Esta é uma troca de pulseira</span>}
          />
        </Form.Group>
        
        {/* Campo de motivo da troca (visível apenas se for uma troca) */}
        {wristbandData.isExchange && (
          <Form.Group className="mb-3">
            <Form.Label>Motivo da Troca <span className="text-danger">*</span></Form.Label>
            <Form.Select
              name="exchangeReason"
              value={wristbandData.exchangeReason}
              onChange={handleWristbandChange}
              isInvalid={wristbandData.isExchange && !wristbandData.exchangeReason && !!wristbandError}
            >
              <option value="">Selecione o motivo</option>
              <option value="2º dia">2º dia</option>
              <option value="3º dia">3º dia</option>
              <option value="4º dia">4º dia</option>
              <option value="Pulseira danificada">Pulseira danificada</option>
              <option value="Pulseira perdida">Pulseira perdida</option>
              <option value="Outro">Outro</option>
            </Form.Select>
            {wristbandData.isExchange && !wristbandData.exchangeReason && wristbandError && (
              <Form.Control.Feedback type="invalid">
                {wristbandError}
              </Form.Control.Feedback>
            )}
          </Form.Group>
        )}
        
        {/* Informação sobre pulseira atual (visível apenas se for uma troca) */}
        {wristbandData.isExchange && selectedPerson.pulseira && (
          <Alert variant="warning" className="mb-3">
            <strong>Pulseira atual:</strong> {selectedPerson.pulseira}
            {wristbandData.exchangeReason && (
              <div className="mt-1">
                <strong>Motivo da troca:</strong> {wristbandData.exchangeReason}
              </div>
            )}
          </Alert>
        )}
        
        {/* Campo de número da pulseira */}
        <Form.Group className="mb-3">
          <Form.Label>
            {wristbandData.isExchange ? 'Nova Pulseira' : 'Número da Pulseira'} 
            <span className="text-danger">*</span>
          </Form.Label>
          <Form.Control
            type="text"
            name="number"
            value={wristbandData.number}
            onChange={handleWristbandChange}
            isInvalid={!!wristbandError && (!wristbandData.isExchange || wristbandData.exchangeReason)}
            placeholder="Digite o número da pulseira"
            autoFocus
          />
          {wristbandError && (!wristbandData.isExchange || wristbandData.exchangeReason) && (
            <Form.Control.Feedback type="invalid">
              {wristbandError}
            </Form.Control.Feedback>
          )}
          <Form.Text className="text-muted">
            Verifique o número impresso na pulseira física antes de registrar.
          </Form.Text>
        </Form.Group>
        
        <div className="d-grid gap-2 mt-4">
          <Button
            variant="success"
            className="py-2"
            onClick={handleSaveWristband}
          >
            <FaSave className="me-2" />
            {wristbandData.isExchange ? 'Registrar Troca' : 'Registrar Entrega'}
          </Button>
        </div>
      </div>
    </div>
  );

  // Renderizar o passo de sucesso
  const renderSuccessStep = () => (
    <div className="wizard-step">
      <div className="text-center py-4">
        <div style={{ 
          width: '120px', 
          height: '120px', 
          borderRadius: '50%', 
          backgroundColor: '#e8f5e9',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          margin: '0 auto 30px auto'
        }}>
          <FaCheckCircle size={60} color="#4caf50" />
        </div>
        
        <h4>Sucesso!</h4>
        <p className="mb-4">
          {wristbandData.isExchange 
            ? 'A troca de pulseira foi registrada com sucesso.' 
            : 'A pulseira foi entregue com sucesso.'}
        </p>
      </div>
    </div>
  );

  // Renderizar o passo atual do wizard
  const renderWizardStep = () => {
    switch (wizardStep) {
      case 2:
        return renderWristbandRegistrationForm();
      case 4:
        return renderSuccessStep();
      default:
        return (
          <div className="wizard-step">
            <div className="text-center py-4">
              <FaInfoCircle size={60} className="text-muted mb-3" />
              <h5>Informação</h5>
              <p>Passo não encontrado.</p>
            </div>
          </div>
        );
    }
  };

  // Estilos para o modal
  const modalStyles = {
    overlay: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 9999,
      padding: '1rem',
      backdropFilter: 'blur(5px)'
    },
    content: {
      position: 'relative',
      width: '100%',
      maxWidth: '650px',
      maxHeight: '90vh',
      display: 'flex',
      flexDirection: 'column',
      backgroundColor: 'white',
      borderRadius: '20px',
      boxShadow: '0 20px 60px rgba(0, 0, 0, 0.15)',
      border: '1px solid rgba(255, 255, 255, 0.2)',
      overflow: 'hidden',
      animationName: 'fadeInUp',
      animationDuration: '0.4s',
      animationTimingFunction: 'cubic-bezier(0.175, 0.885, 0.32, 1.275)',
      animationFillMode: 'both'
    },
    header: {
      background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
      borderBottom: '1px solid rgba(0, 0, 0, 0.05)',
      padding: '1.5rem 2rem',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      flexShrink: 0
    },
    closeButton: {
      width: '36px',
      height: '36px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      borderRadius: '50%',
      border: 'none',
      background: 'white',
      color: '#6c757d',
      fontSize: '1rem',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)'
    },
    title: {
      fontSize: '1.25rem',
      fontWeight: '600',
      color: '#1a1a1a',
      margin: '0',
      display: 'flex',
      alignItems: 'center',
      gap: '0.75rem'
    },
    tabs: {
      display: 'flex',
      borderBottom: '1px solid rgba(0, 0, 0, 0.05)',
      backgroundColor: 'white',
      flexShrink: 0
    },
    tab: {
      padding: '1rem 1.5rem',
      fontSize: '0.95rem',
      fontWeight: '500',
      color: '#6c757d',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      position: 'relative'
    },
    activeTab: {
      color: '#0d6efd',
      fontWeight: '600'
    },
    activeTabIndicator: {
      position: 'absolute',
      bottom: '-1px',
      left: '0',
      right: '0',
      height: '2px',
      backgroundColor: '#0d6efd'
    },
    body: {
      padding: '2rem',
      overflowY: 'auto',
      flex: '1 1 auto'
    },
    footer: {
      display: 'flex',
      justifyContent: 'flex-end',
      padding: '1.5rem 2rem',
      borderTop: '1px solid rgba(0, 0, 0, 0.05)',
      background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
      flexShrink: 0
    },
    footerButton: {
      padding: '0.75rem 1.5rem',
      borderRadius: '50px',
      fontSize: '0.9rem',
      fontWeight: '600',
      transition: 'all 0.3s ease',
      cursor: 'pointer'
    },
    cancelButton: {
      border: '1px solid #dee2e6',
      background: 'white',
      color: '#495057',
      marginRight: '0.75rem'
    },
    submitButton: {
      border: 'none',
      background: '#212529',
      color: 'white',
      boxShadow: '0 4px 10px rgba(0, 0, 0, 0.1)'
    },
    formGroup: {
      marginBottom: '1.5rem'
    },
    formLabel: {
      display: 'block',
      marginBottom: '0.5rem',
      fontSize: '0.9rem',
      fontWeight: '600',
      color: '#495057'
    },
    formInput: {
      width: '100%',
      padding: '0.75rem 1rem',
      border: '1px solid #dee2e6',
      borderRadius: '10px',
      fontSize: '0.9rem',
      color: '#495057',
      transition: 'all 0.3s ease'
    },
    requiredField: {
      color: '#dc3545',
      marginLeft: '0.25rem'
    },
    requiredNotice: {
      fontSize: '0.8rem',
      color: '#dc3545',
      marginBottom: '1rem'
    }
  };

  // Verificar se a pessoa tem entrada ativa (sem saída)
  const hasActiveEntry = (person) => {
    const personId = person.id;
    return presentPeople.some(p => p.id === personId);
  };

  // Renderizar lista de pessoas presentes
  const renderPresentPeople = () => {
    if (!presentPeople || presentPeople.length === 0) {
      return (
        <tr>
          <td colSpan="3" className="text-center py-3">
            <p className="text-muted mb-0">Nenhuma pessoa registrada hoje</p>
          </td>
        </tr>
      );
    }
    
    return presentPeople.map(person => (
      <tr key={`${person.type}-${person.id}`}>
        <td className="font-weight-bold">{person.nome}</td>
        <td>{person.empresa || person.empresa_montadora_nome}</td>
        <td>
          <Badge bg="success" className="modern-badge">Presente</Badge>
          {person.tipo_credencial && (
            <Badge bg="info" className="modern-badge ms-1">{person.tipo_credencial}</Badge>
          )}
        </td>
      </tr>
    ));
  };

  // Renderizar resultados da busca para montadores
  const renderMontadoresResults = () => {
    if (loading) {
      return (
        <div className="text-center py-4">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Carregando...</span>
          </div>
          <p className="mt-2">Buscando montadores...</p>
        </div>
      );
    }
    
    if (!searchResults.length) {
      return (
        <Alert variant="warning">
          <FaExclamationTriangle className="me-2" />
          Nenhum montador encontrado com os critérios de busca.
        </Alert>
      );
    }

    return (
      <div className="modern-table-container">
        <table className="modern-table">
          <thead>
            <tr>
              <th>Nome</th>
              <th>CPF</th>
              <th>Empresa</th>
              <th>Status / Pedido</th>
              <th>Ações</th>
            </tr>
          </thead>
          <tbody>
            {searchResults.map(montador => (
              <tr key={montador.id}>
                <td className="font-weight-bold">{montador.nome}</td>
                <td>{montador.cpf}</td>
                <td>{montador.empresa_montadora_nome}</td>
                <td>{renderStatusBadge(montador)}</td>
                <td>
                  <Button 
                    variant="primary" 
                    size="sm"
                    onClick={() => handleSelectPerson(montador, 'montador')}
                    className="me-1"
                  >
                    <FaIdCard className="me-1" />
                    <span className="d-none d-lg-inline">Verificar</span>
                  </Button>
                  {montador.status === 'ativo' && (
                    <Button 
                      variant="warning" 
                      size="sm"
                      onClick={() => handleTrocarMontador(montador)}
                      className="me-1"
                      title="Trocar Montador"
                    >
                      <FaUserPlus className="me-1" />
                      <span className="d-none d-lg-inline">Trocar Montador</span>
                    </Button>
                  )}
                  <Button 
                    variant="outline-secondary" 
                    size="sm"
                    onClick={() => handleShowHistory(montador, 'montador')}
                  >
                    <FaHistory className="me-1" />
                    <span className="d-none d-lg-inline">Histórico</span>
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  // Renderizar resultados da busca para credenciais
  const renderCredenciaisResults = () => {
    if (loading) {
      return (
        <div className="text-center py-4">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Carregando...</span>
          </div>
          <p className="mt-2">Buscando credenciais...</p>
        </div>
      );
    }
    
    if (!searchResults.length) {
      return (
        <Alert variant="warning">
          <FaExclamationTriangle className="me-2" />
          Nenhuma credencial encontrada com os critérios de busca.
        </Alert>
      );
    }

    return (
      <div className="modern-table-container">
        <table className="modern-table">
          <thead>
            <tr>
              <th>Nome</th>
              <th>CPF</th>
              <th>Empresa</th>
              <th>Tipo</th>
              <th>Status</th>
              <th>Ações</th>
            </tr>
          </thead>
          <tbody>
            {searchResults.map(credencial => (
              <tr key={credencial.id}>
                <td className="font-weight-bold">{credencial.nome}</td>
                <td>{credencial.cpf}</td>
                <td>{credencial.empresa}</td>
                <td>
                  <Badge bg="info" className="modern-badge">
                    {credencial.tipo_credencial}
                  </Badge>
                </td>
                <td>{renderStatusBadge(credencial.status)}</td>
                <td>
                  <Button 
                    variant="primary" 
                    size="sm"
                    onClick={() => handleSelectPerson(credencial, 'credencial')}
                    className="me-1"
                  >
                    <FaIdCard className="me-1" />
                    <span className="d-none d-lg-inline">Verificar</span>
                  </Button>
                  <Button 
                    variant="outline-secondary" 
                    size="sm"
                    onClick={() => handleShowHistory(credencial, 'credencial')}
                  >
                    <FaHistory className="me-1" />
                    <span className="d-none d-lg-inline">Histórico</span>
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  // Função para renderizar o formulário de busca de montadores
  const renderMontadoresBusca = () => {
    return (
      <Row className="align-items-end">
        <Col md={10}>
          <Form.Group className="mb-md-0 mb-3">
            <Form.Label>Nome ou CPF</Form.Label>
            <InputGroup>
              <Form.Control 
                type="text" 
                placeholder="Digite nome ou CPF"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
              />
              <Button 
                variant="primary" 
                onClick={handleSearch}
                disabled={loading}
              >
                {loading ? (
                  <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                ) : (
                  <>
                    <FaSearch className="me-md-2" /> 
                    <span className="d-none d-md-inline">Buscar</span>
                  </>
                )}
              </Button>
            </InputGroup>
          </Form.Group>
        </Col>
        <Col md={2}>
          <Button 
            variant="outline-secondary" 
            className="w-100"
            onClick={() => {
              setSearchTerm('');
              setSearchResults([]);
              setHasSearched(false);
            }}
            disabled={loading}
          >
            Limpar
          </Button>
        </Col>
      </Row>
    );
  };
  
  // Função para renderizar o formulário de busca de credenciais
  const renderCredenciaisBusca = () => {
    return (
      <Row className="align-items-end">
        <Col md={8}>
          <Form.Group className="mb-md-0 mb-3">
            <Form.Label>Nome ou CPF</Form.Label>
            <InputGroup>
              <Form.Control 
                type="text" 
                placeholder="Digite nome ou CPF"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
              />
              <Button 
                variant="primary" 
                onClick={handleSearch}
                disabled={loading}
              >
                {loading ? (
                  <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                ) : (
                  <>
                    <FaSearch className="me-md-2" /> 
                    <span className="d-none d-md-inline">Buscar</span>
                  </>
                )}
              </Button>
            </InputGroup>
          </Form.Group>
        </Col>
        <Col md={2}>
          <Form.Group className="mb-md-0 mb-3">
            <Form.Label>Tipo de Credencial</Form.Label>
            <Form.Select
              name="tipoCredencial"
              value={filters.tipoCredencial}
              onChange={handleFilterChange}
            >
              <option value="">Todos</option>
              {tiposCredencial.map((tipo, index) => (
                <option key={index} value={tipo}>{tipo}</option>
              ))}
            </Form.Select>
          </Form.Group>
        </Col>
        <Col md={2}>
          <Button 
            variant="outline-secondary" 
            className="w-100"
            onClick={() => {
              setSearchTerm('');
              setFilters({ tipoCredencial: '' });
              setSearchResults([]);
              setHasSearched(false);
            }}
            disabled={loading}
          >
            Limpar
          </Button>
        </Col>
      </Row>
    );
  };

  // Função para renderizar a área de busca
  const renderSearchArea = () => {
    return (
      <Card className="modern-table-card mb-4">
        <Card.Header className="modern-card-header">
          <h5 className="modern-card-title">
            <FaSearch className="card-title-icon" />
            Buscar {activeTab === 'montadores' ? 'Montador' : 'Credencial'}
          </h5>
        </Card.Header>
        <Card.Body className="modern-card-body">
          {activeTab === 'montadores' ? renderMontadoresBusca() : renderCredenciaisBusca()}

          <hr className="my-4" />

          {hasSearched && (
            <div>
              <h6>Resultados da Busca</h6>
              {activeTab === 'montadores' ? renderMontadoresResults() : renderCredenciaisResults()}
            </div>
          )}
        </Card.Body>
      </Card>
    );
  };

  // Função para renderizar o card de informações da pessoa selecionada
  const renderPersonInfoCard = () => {
    if (!selectedPerson) return null;
    
    const isPresent = hasActiveEntry(selectedPerson);
    const accessInfo = canAccessToday(selectedPerson);
    
    return (
      <Card className="modern-table-card mb-4">
        <Card.Header className="modern-card-header bg-primary text-white">
          <h5 className="modern-card-title text-white">
            <FaUserCheck className="card-title-icon" />
            Informações do {personType === 'montador' ? 'Montador' : 'Credenciado'}
          </h5>
        </Card.Header>
        <Card.Body className="modern-card-body">
          <div className="text-center mb-4">
            <h4>{selectedPerson.nome}</h4>
            <p className="text-muted mb-0">{selectedPerson.cargo}</p>
            <p className="mb-2">{selectedPerson.empresa || selectedPerson.empresa_montadora_nome}</p>
            
            {personType === 'credencial' && (
              <Badge bg="info" className="mb-2" pill>
                {selectedPerson.tipo_credencial}
              </Badge>
            )}
          </div>

          <ListGroup className="mb-4 shadow-sm rounded">
            <ListGroup.Item className="border-top-0 border-left-0 border-right-0">
              <strong>CPF:</strong> {selectedPerson.cpf}
            </ListGroup.Item>
            <ListGroup.Item className="border-left-0 border-right-0">
              <strong>Status:</strong> {renderStatusBadge(selectedPerson)}
            </ListGroup.Item>
            {selectedPerson.pedido_id && (
              <ListGroup.Item className="border-left-0 border-right-0">
                <strong>Pedido:</strong> #{selectedPerson.pedido_id} 
                <Badge 
                  bg={selectedPerson.pedido_status === 'aprovado' ? 'success' : 
                      selectedPerson.pedido_status === 'pendente' ? 'warning' : 'danger'} 
                  className="ms-2"
                >
                  {selectedPerson.pedido_status || 'Sem status'}
                </Badge>
              </ListGroup.Item>
            )}
            {selectedPerson.pulseira && (
              <ListGroup.Item className="border-left-0 border-right-0">
                <strong>Pulseira:</strong> {selectedPerson.pulseira}
              </ListGroup.Item>
            )}
          </ListGroup>

          {!accessInfo.canAccess && (
            <Alert variant="danger" className="d-flex align-items-center">
              <FaTimesCircle className="me-2" size={24} />
              <div>
                <strong>Acesso Negado</strong>
                <p className="mb-0">{accessInfo.reason}</p>
                {selectedPerson.pedido_status === 'pendente' && (
                  <p className="mb-0 mt-1">
                    <small>Aguardando aprovação do administrador.</small>
                  </p>
                )}
              </div>
            </Alert>
          )}

          <div className="d-grid gap-2 mt-4">          
            <Button 
              variant="outline-primary"
              onClick={handleOpenWristbandModal}
              className="py-2"
              disabled={!accessInfo.canAccess}
            >
              <FaPrint className="me-2" />
              {selectedPerson.pulseira ? 'TROCAR PULSEIRA' : 'ENTREGAR PULSEIRA'}
            </Button>
            
            <Button 
              variant="outline-secondary"
              onClick={() => {
                setSelectedPerson(null);
                setPersonType(null);
              }}
              className="py-2"
            >
              Fechar
            </Button>
          </div>
        </Card.Body>
      </Card>
    );
  };

  // Adicionar efeito para estilos CSS
  useEffect(() => {
    const style = document.createElement('style');
    style.innerHTML = `
      @keyframes fadeInUp {
        from { 
          opacity: 0; 
          transform: translateY(30px);
        }
        to { 
          opacity: 1; 
          transform: translateY(0);
        }
      }
      
      .form-input {
        width: 100%;
        padding: 0.75rem 1rem;
        border: 1px solid #dee2e6;
        border-radius: 10px;
        font-size: 0.9rem;
        color: #495057;
        transition: all 0.3s ease;
      }
      
      .form-input:focus {
        outline: none;
        border-color: #212529;
        box-shadow: 0 0 0 3px rgba(33, 37, 41, 0.1);
      }
      
      .form-input:disabled {
        background-color: #f8f9fa;
        color: #6c757d;
        cursor: not-allowed;
      }
      
      .form-label {
        display: block;
        margin-bottom: 0.5rem;
        font-size: 0.9rem;
        font-weight: 600;
        color: #495057;
      }
      
      .form-group {
        margin-bottom: 1.5rem;
      }
      
      .modal-close-btn:hover {
        background-color: #f8f9fa;
        color: #212529;
        transform: rotate(90deg);
      }
      
      .btn-cancel:hover {
        background-color: #f8f9fa;
      }
      
      .btn-submit:hover {
        background-color: #343a40;
        transform: translateY(-2px);
        box-shadow: 0 6px 15px rgba(0, 0, 0, 0.15);
      }
      
      .modal-tab {
        position: relative;
        padding: 1rem 1.5rem;
        font-weight: 500;
        color: #6c757d;
        cursor: pointer;
        transition: all 0.3s ease;
      }
      
      .modal-tab-active {
        color: #0d6efd;
        font-weight: 600;
      }
      
      .modal-tab-active::after {
        content: '';
        position: absolute;
        bottom: -1px;
        left: 0;
        right: 0;
        height: 2px;
        background-color: #0d6efd;
      }
      
      .modal-tab:hover:not(.modal-tab-active) {
        color: #495057;
        background-color: rgba(0, 0, 0, 0.02);
      }

      .empresa-info {
        margin-top: 0.5rem;
      }
      
      .empresa-card {
        background-color: #f8f9fa;
        border: 1px solid #dee2e6;
        border-radius: 10px;
        padding: 0.75rem 1rem;
        color: #6c757d;
        display: flex;
        align-items: center;
        gap: 0.5rem;
        font-weight: 500;
      }
      
      .empresa-card .empresa-icon {
        color: #28a745;
      }

      .produto-card {
        display: flex;
        align-items: center;
        padding: 0.75rem;
        border: 1px solid #28a745;
        border-radius: 8px;
        margin-bottom: 0.5rem;
        background: #f8fff9;
        cursor: not-allowed;
        transition: all 0.2s ease;
      }
      
      .produto-card.selected {
        border-color: #28a745;
        background-color: #f8fff9;
        box-shadow: 0 2px 8px rgba(40, 167, 69, 0.15);
      }
      
      .produto-image {
        width: 40px;
        height: 40px;
        border-radius: 6px;
        background-color: #f8f9fa;
        display: flex;
        align-items: center;
        justify-content: center;
        margin-right: 0.75rem;
        flex-shrink: 0;
      }
      
      .produto-info {
        flex: 1;
      }
      
      .produto-nome {
        font-weight: 600;
        color: #212529;
        font-size: 0.9rem;
      }
    `;
    document.head.appendChild(style);
    
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  // Impedir o scroll quando os modals estiverem abertos
  useEffect(() => {
    if (showWristbandModal || showHistoryModal || showTrocaMontadorModal) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }
    
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [showWristbandModal, showHistoryModal, showTrocaMontadorModal]);

  return (
    <div className="dashboard-container">
      <div className="dashboard-hub">
        <div className="dashboard-header">
          <div className="dashboard-title-section">
            <h1 className="dashboard-main-title">
              <FaLock className="dashboard-title-icon" />
              Controle de Acesso
            </h1>
            <p className="dashboard-subtitle">
              Gerencie e monitore o acesso de montadores e credenciais ao evento
            </p>
          </div>
        </div>

        <div className="dashboard-section-header">
          <h2 className="dashboard-section-title">
            <FaUsersCog className="dashboard-section-icon" />
            Verificação de Acesso
          </h2>
          <p className="dashboard-section-subtitle">
            Verifique e registre o acesso de pessoas no evento
          </p>
        </div>

        {/* Tabs para alternar entre montadores e credenciais */}
        <Nav variant="tabs" className="mb-4">
          <Nav.Item>
            <Nav.Link 
              active={activeTab === 'montadores'} 
              onClick={() => {
                setActiveTab('montadores');
                setSearchTerm('');
                setFilters({ tipoCredencial: '' });
                setSearchResults([]);
                setHasSearched(false);
                setSelectedPerson(null);
                setPersonType(null);
              }}
              className="d-flex align-items-center"
            >
              <FaBuilding className="me-2" />
              Montadores
            </Nav.Link>
          </Nav.Item>
          {/*<Nav.Item>
            <Nav.Link 
              active={activeTab === 'credenciais'} 
              onClick={() => {
                setActiveTab('credenciais');
                setSearchTerm('');
                setFilters({ tipoCredencial: '' });
                setSearchResults([]);
                setHasSearched(false);
                setSelectedPerson(null);
                setPersonType(null);
              }}
              className="d-flex align-items-center"
            >
              <FaAddressCard className="me-2" />
              Credenciais
            </Nav.Link>
          </Nav.Item>*/}
        </Nav>

        <Row>
          <Col lg={8}>
            {/* Área de busca */}
            {renderSearchArea()}
          </Col>

          <Col lg={4}>
            {/* Card de informações da pessoa */}
            {selectedPerson ? (
              renderPersonInfoCard()
            ) : (
              showHistory ? (
                <Card className="modern-table-card mb-4">
                  <Card.Header className="modern-card-header bg-secondary text-white">
                    <div className="d-flex justify-content-between align-items-center">
                      <h5 className="modern-card-title text-white mb-0">
                        <FaHistory className="card-title-icon" />
                        Histórico de Acesso
                      </h5>
                      <Button 
                        variant="light" 
                        size="sm"
                        onClick={() => setShowHistory(false)}
                      >
                        Fechar
                      </Button>
                    </div>
                  </Card.Header>
                  <Card.Body className="modern-card-body">
                    <div className="text-center mb-3">
                      <h5>{selectedPerson?.nome}</h5>
                      <p className="text-muted mb-0">{selectedPerson?.empresa || selectedPerson?.empresa_montadora_nome}</p>
                    </div>

                    <ul className="nav nav-tabs mb-3">
                      <li className="nav-item">
                        <a className="nav-link active" href="#access-tab" data-bs-toggle="tab">
                          Acessos
                        </a>
                      </li>
                      <li className="nav-item">
                        <a className="nav-link" href="#wristband-tab" data-bs-toggle="tab">
                          Pulseiras
                        </a>
                      </li>
                    </ul>

                    <div className="tab-content">
                      <div className="tab-pane active" id="access-tab">
                        {accessHistory.filter(access => access.montador_id === selectedPerson?.id).length > 0 ? (
                          <div className="modern-table-container">
                            <table className="modern-table">
                              <thead>
                                <tr>
                                  <th>Data</th>
                                  <th>Hora</th>
                                  <th>Tipo</th>
                                  {/*<th>Registrado por</th>*/}
                                </tr>
                              </thead>
                              <tbody>
                                {accessHistory
                                  .filter(access => access.montador_id === selectedPerson?.id)
                                  .map(access => (
                                    <tr key={access.id}>
                                      <td>{formatDate(access.data_entrada)}</td>
                                      <td>{new Date(access.data_entrada).toLocaleTimeString()}</td>
                                      <td>
                                        <Badge bg={!access.data_saida ? 'success' : 'warning'} className="modern-badge">
                                          {!access.data_saida ? 'Entrada' : 'Saída'}
                                        </Badge>
                                      </td>
                                      {/*<td>{access.registrado_por}</td>*/}
                                    </tr>
                                  ))}
                              </tbody>
                            </table>
                          </div>
                        ) : (
                          <Alert variant="info">
                            Nenhum registro de acesso encontrado.
                          </Alert>
                        )}
                      </div>
                      <div className="tab-pane" id="wristband-tab">
                        <WristbandTimeline 
                          history={wristbandHistory.filter(history => 
                            personType === 'montador' 
                              ? history.montador_id === selectedPerson?.id
                              : history.credencial_id === selectedPerson?.id
                          )} 
                        />
                      </div>
                    </div>
                  </Card.Body>
                </Card>
              ) : (
                <Card className="modern-table-card mb-4">
                  <Card.Body className="modern-card-body text-center py-5">
                    <FaUserCheck size={60} className="text-muted mb-3" />
                    <h5>Verificação de Acesso</h5>
                    <p className="text-muted">
                      Busque um {activeTab === 'montadores' ? 'montador' : 'credenciado'} pelo nome ou CPF para verificar o acesso.
                    </p>
                  </Card.Body>
                </Card>
              )
            )}

            {/* Card de pessoas presentes hoje */}
            <Card className="modern-table-card">
              <Card.Header className="modern-card-header">
                <h5 className="modern-card-title">
                  <FaUserCheck className="card-title-icon" />
                  Pessoas Presentes Hoje
                </h5>
              </Card.Header>
              <Card.Body className="modern-card-body">
                {loading ? (
                  <div className="text-center py-3">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Carregando...</span>
                    </div>
                    <p className="mt-2">Carregando pessoas presentes...</p>
                  </div>
                ) : (
                  <div className="modern-table-container">
                    <table className="modern-table">
                      <thead>
                        <tr>
                          <th>Nome</th>
                          <th>Empresa</th>
                          <th>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {renderPresentPeople()}
                      </tbody>
                    </table>
                  </div>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Modal de Entrega/Troca de Pulseira */}
        {showWristbandModal && selectedPerson && (
          <div style={modalStyles.overlay}>
            <div style={{...modalStyles.content, maxWidth: '550px'}}>
              <div style={modalStyles.header}>
                <h2 style={modalStyles.title}>
                  <FaTags style={{ color: '#6c757d' }} />
                  {wristbandData.isExchange ? 'Troca de Pulseira' : 'Entrega de Pulseira'}
                </h2>
                <button 
                  onClick={() => setShowWristbandModal(false)}
                  style={modalStyles.closeButton}
                  className="modal-close-btn"
                  aria-label="Fechar"
                >
                  ✕
                </button>
              </div>
              
              <div style={modalStyles.body}>
                {/* Informações da pessoa */}
                <div className="mb-4 p-3 bg-light rounded">
                  <div className="d-flex align-items-center mb-2">
                    <div>
                      <h5 className="mb-0">{selectedPerson.nome}</h5>
                      <p className="text-muted mb-0">
                        {selectedPerson.empresa || selectedPerson.empresa_montadora_nome} - {selectedPerson.cargo}
                      </p>
                      {personType === 'credencial' && (
                        <Badge bg="info" className="mt-1">
                          {selectedPerson.tipo_credencial}
                        </Badge>
                      )}
                    </div>
                  </div>
                </div>
                
                {/* Conteúdo do wizard step */}
                <div key={wizardStep} className="wizard-step-container" style={{
                  transition: 'all 0.3s ease'
                }}>
                  {renderWizardStep()}
                </div>
              </div>
              
              {wizardStep === 2 && (
                <div style={modalStyles.footer}>
                  <Button 
                    variant="outline-secondary"
                    onClick={() => setShowWristbandModal(false)}
                  >
                    Cancelar
                  </Button>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Modal de Histórico */}
        <HistoryModal 
          showModal={showHistoryModal}
          handleCloseModal={handleCloseHistoryModal}
          selectedPerson={selectedPerson}
          personType={personType}
          accessHistory={accessHistory}
          wristbandHistory={wristbandHistory}
          formatDate={formatDate}
        />

        {/* NOVO MODAL DE TROCA DE MONTADOR */}
        {showTrocaMontadorModal && montadorParaTroca && (
          <div style={modalStyles.overlay}>
            <div style={modalStyles.content}>
              <div style={modalStyles.header}>
                <h2 style={modalStyles.title}>
                  <FaUserPlus style={{ color: '#6c757d' }} />
                  Trocar Montador
                </h2>
                <button 
                  onClick={() => setShowTrocaMontadorModal(false)}
                  style={modalStyles.closeButton}
                  className="modal-close-btn"
                  aria-label="Fechar"
                >
                  ✕
                </button>
              </div>

              <div style={modalStyles.tabs}>
                <div 
                  className={`modal-tab ${activeTrocaTab === 'dados' ? 'modal-tab-active' : ''}`}
                  onClick={() => handleTrocaTabClick('dados')}
                >
                  Dados do Novo Montador
                </div>
                <div 
                  className={`modal-tab ${activeTrocaTab === 'montador' ? 'modal-tab-active' : ''}`}
                  onClick={() => handleTrocaTabClick('montador')}
                >
                  Detalhes & Motivo
                </div>
              </div>
              
              <div style={modalStyles.body} className="modal-scrollable-body">
                {/* Informações do montador sendo substituído */}
                <div className="mb-4 p-3 bg-warning bg-opacity-10 border border-warning rounded">
                  <div className="d-flex align-items-center">
                    <FaExchangeAlt className="me-2 text-warning" />
                    <div>
                      <h6 className="mb-0">Montador sendo substituído:</h6>
                      <strong>{montadorParaTroca.nome}</strong> - {montadorParaTroca.cpf}
                      <br />
                      <small className="text-muted">{montadorParaTroca.empresa_montadora_nome}</small>
                      {/* NOVO - Mostrar informações do pedido */}
                      {montadorParaTroca.pedido_id && (
                        <>
                          <br />
                          <span className="badge bg-info mt-1">
                            Pedido #{montadorParaTroca.pedido_id} - {montadorParaTroca.pedido_status || 'Status não informado'}
                          </span>
                        </>
                      )}
                      {!montadorParaTroca.pedido_id && (
                        <>
                          <br />
                          <span className="badge bg-secondary mt-1">
                            Sem pedido vinculado
                          </span>
                        </>
                      )}
                    </div>
                  </div>
                </div>

                {activeTrocaTab === 'dados' && (
                  <>
                    <div className="form-group">
                      <label className="form-label" htmlFor="empresa_montadora_id">
                        Empresa Montadora <span style={modalStyles.requiredField}>*</span>
                      </label>
                      {renderTrocaEmpresaMontadoraField()}
                      <small style={{ color: '#6c757d', fontSize: '0.8rem', marginTop: '0.25rem', display: 'block' }}>
                        Selecione a empresa montadora responsável pelo novo montador
                      </small>
                    </div>

                    <div className="form-group">
                      <label className="form-label" htmlFor="evento_id">
                        Evento <span style={modalStyles.requiredField}>*</span>
                      </label>
                      {renderTrocaEventosDropdown()}
                    </div>
                    
                    <div className="form-group">
                      <label className="form-label" htmlFor="nome">
                        Nome Completo <span style={modalStyles.requiredField}>*</span>
                      </label>
                      <input
                        type="text"
                        id="nome"
                        name="nome"
                        value={trocaFormData.nome}
                        onChange={handleTrocaChange}
                        className="form-input"
                        placeholder="Digite o nome completo do novo montador"
                        required
                      />
                    </div>
                    
                    <div className="form-group">
                      <label className="form-label" htmlFor="cpf">
                        CPF <span style={modalStyles.requiredField}>*</span>
                      </label>
                      <input
                        type="text"
                        id="cpf"
                        name="cpf"
                        value={trocaFormData.cpf}
                        onChange={handleTrocaChange}
                        className="form-input"
                        placeholder="000.000.000-00"
                        required
                      />
                    </div>
                    
                    <div style={modalStyles.requiredNotice}>
                      * Campos obrigatórios
                    </div>
                  </>
                )}
                
                {activeTrocaTab === 'montador' && (
                  <>
                    <div className="form-group">
                      <label className="form-label" htmlFor="motivo">
                        <FaExchangeAlt className="me-2" />
                        Motivo da Troca <span style={modalStyles.requiredField}>*</span>
                      </label>
                      <select
                        id="motivo"
                        name="motivo"
                        value={trocaFormData.motivo}
                        onChange={handleTrocaChange}
                        className="form-input"
                        required
                      >
                        <option value="">Selecione o motivo da troca</option>
                        {motivosTroca.map((motivo, index) => (
                          <option key={index} value={motivo}>{motivo}</option>
                        ))}
                      </select>
                    </div>

                    <div className="form-group">
                      <label className="form-label" htmlFor="observacoes">
                        Observações Adicionais
                      </label>
                      <textarea
                        id="observacoes"
                        name="observacoes"
                        value={trocaFormData.observacoes}
                        onChange={handleTrocaChange}
                        className="form-input"
                        rows="3"
                        placeholder="Digite observações adicionais sobre a troca (opcional)"
                      />
                    </div>

                    <div className="form-group">
                      <label className="form-label" htmlFor="produto">
                        <FaBox className="me-2" />
                        Produtos Vinculados ao Montador
                      </label>
                      {renderTrocaProdutosDropdown()}
                      <small style={{ color: '#6c757d', fontSize: '0.8rem', marginTop: '0.25rem', display: 'block' }}>
                        Produtos automaticamente vinculados a montadores (apenas para visualização)
                      </small>
                    </div>
                    
                    <div style={{
                      backgroundColor: '#fff3cd',
                      color: '#856404',
                      padding: '1rem',
                      borderRadius: '10px',
                      border: '1px solid #ffeeba',
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: '0.5rem',
                      marginBottom: '1rem'
                    }}>
                      <FaExclamationTriangle style={{marginTop: '0.2rem'}} />
                      <div>
                        <strong>Importante:</strong> O montador anterior será marcado como inativo e o novo será criado como ativo. 
                        Esta ação será registrada no histórico de substituições para auditoria.
                      </div>
                    </div>
                    
                    <div style={modalStyles.requiredNotice}>
                      * Campos obrigatórios
                    </div>
                  </>
                )}
              </div>
              
              <div style={modalStyles.footer}>
                <button
                  type="button"
                  onClick={() => setShowTrocaMontadorModal(false)}
                  style={{...modalStyles.footerButton, ...modalStyles.cancelButton}}
                  className="btn-cancel"
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  onClick={handleSubmitTrocaMontador}
                  style={{
                    ...modalStyles.footerButton,
                    ...modalStyles.submitButton,
                    background: '#ffc107',
                    color: '#212529'
                  }}
                  className="btn-submit"
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                      Processando...
                    </>
                  ) : (
                    <>
                      <FaUserPlus className="me-2" />
                      Confirmar Troca
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ControleAcesso;